import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { z } from "zod";

const JOIN_CLIPBOARD_GUIDES_CHANNEL_REQUEST_SCHEMA = z.object({
  workerId: z.string(),
  shiftId: z.string(),
});

type JoinClipboardGuidesChannelRequest = z.infer<
  typeof JOIN_CLIPBOARD_GUIDES_CHANNEL_REQUEST_SCHEMA
>;

const JOIN_CLIPBOARD_GUIDES_CHANNEL_RESPONSE_SCHEMA = z.object({
  data: z.object({
    type: z.literal("clipboard-guides"),
    attributes: z.object({
      channelUrl: z.string(),
    }),
  }),
});

type JoinClipboardGuidesChannelResponse = z.infer<
  typeof JOIN_CLIPBOARD_GUIDES_CHANNEL_RESPONSE_SCHEMA
>;

interface UseJoinClipboardGuidesChannelParams {
  workplaceId: string;
  options?: UseMutationOptions<
    JoinClipboardGuidesChannelResponse,
    unknown,
    JoinClipboardGuidesChannelRequest
  >;
}

type UseJoinClipboardGuidesChannelResult = UseMutationResult<
  JoinClipboardGuidesChannelResponse,
  unknown,
  JoinClipboardGuidesChannelRequest
>;

export function useJoinClipboardGuidesChannel(
  params: UseJoinClipboardGuidesChannelParams
): UseJoinClipboardGuidesChannelResult {
  const { workplaceId, options = {} } = params;

  return useMutation({
    mutationFn: async (data: JoinClipboardGuidesChannelRequest) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/clipboard-guides/channels/${workplaceId}/members`,
        requestSchema: JOIN_CLIPBOARD_GUIDES_CHANNEL_REQUEST_SCHEMA,
        responseSchema: JOIN_CLIPBOARD_GUIDES_CHANNEL_RESPONSE_SCHEMA,
        data,
      });
      return response.data;
    },
    ...options,
  });
}
